import { useContext, useEffect, useRef, useState } from 'react';

import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';

import { ENUM_EVENT_SOCKET } from 'constants/enum';
import { useNetwork } from 'hooks/useNetwork';
import Message from 'pages/Chat/_components/Message';
import { ChatContext } from 'pages/Chat/Chats';
import { setPaginationMessagesHistory } from 'store/actions/chat';
import { clearModal, setModal } from 'store/actions/common';
import { convertSvgToBase64, handleError, scrollToBottomByRef, scrollToById } from 'utils/helper';

const ExclamationCircle = require('assets/resources/ExclamationCircle.svg').default;
const LeadingIcon = require('assets/resources/LeadingIcon.svg').default;
const SendIcon = require('assets/resources/SendIcon.svg').default;

const ScreenChat = () => {
  const dispatch = useDispatch();
  const { sendMessage, message, setMessage } = useContext(ChatContext);

  const { me } = useSelector((state) => state.auth);
  const { dataCurrentConversation, messagesHistory, babyId } = useSelector((state) => state.chat);

  const { isOnline } = useNetwork();

  const refChat = useRef(null);
  const refChatHeader = useRef(null);
  const refChatPanel = useRef(null);
  const refChatContent = useRef(null);
  const textareaRef = useRef(null);

  const [heightChat, setHeightChat] = useState(0);
  const [currentIsScrollToBottom, setCurrentIsScrollToBottom] = useState(false);
  const [exclamationCircleBase64, setExclamationCircleBase64] = useState(null);

  useEffect(() => {
    const convertSvg = async () => {
      const base64 = await convertSvgToBase64(ExclamationCircle);
      setExclamationCircleBase64(base64);
    };

    convertSvg();
  }, []);

  // CALCULATE HEIGHT CHAT
  useEffect(() => {
    if (refChat.current && refChatHeader.current) {
      let heightPanel = null;
      let panelStyle = null;
      if (dataCurrentConversation?.participant_id !== -1 && refChatPanel.current) {
        heightPanel = refChatPanel.current;
        panelStyle = window.getComputedStyle(heightPanel);
      }
      const heightChatWrapper = refChat.current;
      const heightChatWrapperStyle = window.getComputedStyle(heightChatWrapper);

      const heightHeader = refChatHeader.current;
      const headerStyle = window.getComputedStyle(heightHeader);

      const heightChat =
        heightChatWrapper.clientHeight -
        parseFloat(heightChatWrapperStyle.paddingTop) -
        parseFloat(heightChatWrapperStyle.paddingBottom) -
        (heightHeader.clientHeight + parseFloat(headerStyle.marginTop) + parseFloat(headerStyle.marginBottom)) -
        ((heightPanel?.clientHeight || 0) + parseFloat(panelStyle?.marginTop || 0) + parseFloat(panelStyle?.marginBottom || 0));

      setHeightChat(heightChat);
    }
  }, [refChat.current?.clientHeight, refChatHeader.current?.clientHeight, refChatPanel.current?.clientHeight]);

  // SCROLL TO BOTTOM WHEN NEW MESSAGE
  const handleScrollToBottom = () => {
    if (currentIsScrollToBottom) {
      scrollToBottomByRef(refChatContent);
      setCurrentIsScrollToBottom(true);
      return;
    }
  };

  useEffect(() => {
    if (!currentIsScrollToBottom) {
      scrollToBottomByRef(refChatContent);
      setCurrentIsScrollToBottom(true);
    }
  }, []);

  useEffect(() => {
    handleScrollToBottom();
  }, [refChatPanel.current?.clientHeight, messagesHistory]);

  // SEND MESSAGE
  const handleSend = () => {
    if (!isOnline) {
      dispatch(
        setModal({
          isOpen: true,
          type: 'confirm',
          title: '通信エラーが発生しました。ネットワーク接続をご確認ください。',
          confirmText: '',
          cancelText: '閉じる',
          icon: exclamationCircleBase64,
          onConfirm: () => {
            dispatch(clearModal());
          },
          onCancel: () => {
            dispatch(clearModal());
          },
        })
      );

      return;
    }
    const _message = textareaRef.current.value.trim();

    if (!_message) return;

    sendMessage(ENUM_EVENT_SOCKET.SEND_MESSAGE, _message, dataCurrentConversation?.conversation_id, dataCurrentConversation?.participant_id, babyId)
      .then(() => {
        setMessage({
          ...message,
          [dataCurrentConversation?.conversation_id]: '',
        });
        textareaRef.current.style.height = '52px';
        textareaRef.current.focus();
      })
      .catch((error) => {
        handleError(error);
      });
    setMessage({
      ...message,
      [dataCurrentConversation?.conversation_id]: '',
    });
    scrollToById('list-conversations', 'top');
  };

  const handleInputMessage = (e) => {
    const textarea = e.target;

    const padding = 24;
    const minContentHeight = 28;

    textarea.style.height = '52px';

    const contentHeight = textarea.scrollHeight - padding;

    if (contentHeight > minContentHeight) {
      const newHeight = Math.min(contentHeight + padding, 160);
      textarea.style.height = newHeight + 'px';
      if (contentHeight + padding > 160) {
        textareaRef.current.style.overflow = 'auto';
      } else {
        textareaRef.current.style.overflow = 'hidden';
      }
    }
  };

  // CHECK SCROLL IN END TOP
  const checkScrollInEnd = () => {
    if (refChatContent.current) {
      if (Number(refChatContent.current.scrollHeight - refChatContent.current.scrollTop) - 10 <= Number(refChatContent.current.clientHeight)) {
        setCurrentIsScrollToBottom(true);
      } else {
        setCurrentIsScrollToBottom(false);
      }
      if (refChatContent.current.scrollTop <= 0) {
        dispatch((dispatch, getState) => {
          const paginationMessagesHistory = getState().chat.paginationMessagesHistory;
          dispatch(
            setPaginationMessagesHistory({
              ...paginationMessagesHistory,
              ...(paginationMessagesHistory.page + 1 <= Math.ceil(paginationMessagesHistory.total / paginationMessagesHistory.page_size)
                ? { page: paginationMessagesHistory.page + 1 }
                : {}),
            })
          );
        });
      }
    }
  };

  // ADD EVENT LISTENER SCROLL
  useEffect(() => {
    if (refChatContent.current) {
      refChatContent.current.addEventListener('scroll', checkScrollInEnd);
    }
    return () => {
      if (refChatContent.current) {
        refChatContent.current.removeEventListener('scroll', checkScrollInEnd);
      }
    };
  }, [refChatContent?.current, messagesHistory]);

  // ADD EVENT ENTER AND SHIFT ENTER IF Textarea is focused
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    };
    if (textareaRef.current && textareaRef.current.focus) {
      textareaRef.current.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      if (textareaRef.current) {
        textareaRef.current.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [textareaRef.current, dataCurrentConversation?.conversation_id, dataCurrentConversation?.participant_id, babyId]);

  return (
    <div className="py-6 px-4 overflow-y-hidden h-full">
      <div ref={refChat} className="relative w-full h-full">
        <div ref={refChatHeader} id="chat-header" className="px-4 mb-6">
          <h3 className="font-bold text-2xl text-black/85">
            {Number(dataCurrentConversation?.participant_id) !== -1 && <span>{dataCurrentConversation?.participant_id}&nbsp;-&nbsp;</span>}
            <span>{dataCurrentConversation?.participant_name || '-'}</span>
          </h3>
        </div>

        <div ref={refChatContent} className="flex flex-col overflow-y-auto px-4" style={{ height: `${heightChat}px` }}>
          {Object.entries(messagesHistory).map(([date, messages], idx) => (
            <div key={idx}>
              <div className="text-sm text-black-102 mb-2.5 flex justify-center items-center">
                <div className="text-sm font-medium flex justify-center items-center bg-gray-651 rounded-lg w-30">
                  {moment(date).isSame(moment(), 'day') ? 'Today' : date}
                </div>
              </div>
              {messages.map((item, idx) => (
                <Message key={idx} item={item} is_me={item.sender_id === me?.id} />
              ))}
            </div>
          ))}
        </div>

        {dataCurrentConversation?.participant_id !== -1 && dataCurrentConversation?.status === true && (
          <div ref={refChatPanel} id="chat-panel" className="chat-panel mt-2 px-4 absolute bottom-0 left-0 right-0">
            <div className="flex items-center gap-2 relative">
              <div className="relative w-full">
                <div className="icon-chat-panel absolute left-2 top-4.5">
                  <img width={18} height={18} src={LeadingIcon} alt="icon-leading" />
                </div>
                <textarea
                  ref={textareaRef}
                  style={{
                    height: '52px',
                    minHeight: '52px',
                  }}
                  value={message[dataCurrentConversation?.conversation_id] || ''}
                  onChange={(e) => {
                    setMessage({
                      ...message,
                      [dataCurrentConversation?.conversation_id]: e.target.value,
                    });
                  }}
                  onInput={handleInputMessage}
                  type="text"
                  className="focus:outline-none p-3 pl-7 w-full max-h-40 resize-none rounded border border-solid border-purple-536 inset-0"
                />
              </div>
              <div
                className={`icon-chat-panel flex items-center justify-center w-8.5 h-9.5 ${message[dataCurrentConversation?.conversation_id]?.trim() ? '' : 'grayscale'}`}
              >
                <img onClick={handleSend} width={18} height={18} src={SendIcon} alt="icon-send" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScreenChat;
